import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

/**
 * Stato delle liste admin (ricerca, filtri, pagina, ordinamento) tenuto
 * nell'URL: sopravvive all'apertura di un dettaglio e al tasto Indietro del
 * browser, ed è condivisibile come link.
 *
 * - `get(key, fallback)` legge un parametro.
 * - `set(patch)` aggiorna più parametri insieme (valore vuoto/null = rimosso);
 *   qualsiasi modifica che non tocca esplicitamente `page` riporta a pagina 1.
 * - Si usa sempre `replace: true` per non riempire la history a ogni tasto.
 */
export function useListUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const get = useCallback(
    (key: string, fallback = '') => searchParams.get(key) ?? fallback,
    [searchParams],
  );

  const set = useCallback(
    (patch: Record<string, string | number | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null || value === undefined || value === '') next.delete(key);
            else next.set(key, String(value));
          }
          if (!('page' in patch)) next.delete('page');
          if (next.get('page') === '1') next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const pageRaw = Number(searchParams.get('page'));
  const page = Number.isInteger(pageRaw) && pageRaw > 0 ? pageRaw : 1;

  return { get, set, page, searchParams };
}

/**
 * Input di ricerca reattivo con commit debounced verso l'URL.
 * L'input resta locale (niente lag mentre si scrive); se il valore nell'URL
 * cambia dall'esterno (Indietro del browser, link) l'input si riallinea.
 */
export function useDebouncedUrlSearch(
  urlValue: string,
  commit: (value: string) => void,
  delayMs = 300,
) {
  const [input, setInput] = useState(urlValue);
  const lastCommitted = useRef(urlValue);
  const commitRef = useRef(commit);
  commitRef.current = commit;

  // URL cambiato dall'esterno → riallinea l'input.
  useEffect(() => {
    if (urlValue !== lastCommitted.current) {
      lastCommitted.current = urlValue;
      setInput(urlValue);
    }
  }, [urlValue]);

  useEffect(() => {
    const trimmed = input.trim();
    if (trimmed === lastCommitted.current) return undefined;
    const timeout = window.setTimeout(() => {
      lastCommitted.current = trimmed;
      commitRef.current(trimmed);
    }, delayMs);
    return () => window.clearTimeout(timeout);
  }, [input, delayMs]);

  return [input, setInput] as const;
}

/**
 * Destinazione del link "Indietro" di una pagina di dettaglio: la lista da cui
 * si è arrivati (con i suoi filtri/pagina, passata come `state.from` dal Link)
 * oppure la lista di default. Accetta solo percorsi che iniziano con `fallback`
 * per non rimandare altrove con uno state estraneo.
 */
export function useBackTo(fallback: string): string {
  const location = useLocation();
  const from = (location.state as { from?: unknown } | null)?.from;
  if (typeof from === 'string' && (from === fallback || from.startsWith(`${fallback}?`))) {
    return from;
  }
  return fallback;
}
